import type {
  EntityId,
  JsonValue,
  KnowledgeItem,
} from "@note-app/contracts";
import {
  ValidationError,
  createFolder,
  createTag,
  type Clock,
  type Folder,
  type IdGenerator,
  type Tag,
} from "@note-app/domain";
import {
  EntityNotFoundError,
  type FolderUpdatePatch,
  type ItemUpdatePatch,
  type TagUpdatePatch,
  type UnitOfWork,
} from "@note-app/storage";

export const FAVORITE_PROPERTY = "fengyue.favorite";

export interface CatalogDependencies {
  readonly unitOfWork: UnitOfWork;
  readonly clock: Clock;
  readonly ids: IdGenerator;
}

export class FolderApplicationService {
  public constructor(private readonly dependencies: CatalogDependencies) {}

  public executeCreate(input: {
    name: string;
    parentId?: EntityId;
    position?: number;
  }): Promise<Folder> {
    const folder = createFolder({
      id: this.dependencies.ids.next(),
      name: input.name,
      createdAt: this.dependencies.clock.now(),
      ...(input.parentId === undefined ? {} : { parentId: input.parentId }),
      ...(input.position === undefined ? {} : { position: input.position }),
    });
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      await repositories.folders.create(folder);
      return folder;
    });
  }

  public executeUpdate(input: {
    folderId: EntityId;
    expectedRevision: number;
    name?: string;
    parentId?: EntityId | null;
    position?: number;
  }): Promise<Folder> {
    assertExpectedRevision(input.expectedRevision);
    const patch: FolderUpdatePatch = {
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(!("parentId" in input) ? {} : { parentId: input.parentId ?? null }),
      ...(input.position === undefined ? {} : { position: input.position }),
    };
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const current = await repositories.folders.get(input.folderId);
      if (current === null) {
        throw new EntityNotFoundError("folder", input.folderId);
      }
      return repositories.folders.update(
        input.folderId,
        patch,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, current.updatedAt),
      );
    });
  }

  public executeTrash(input: {
    folderId: EntityId;
    expectedRevision: number;
  }): Promise<Folder> {
    assertExpectedRevision(input.expectedRevision);
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const folder = await requireFolder(
        repositories,
        input.folderId,
        true,
      );
      return repositories.folders.moveToTrash(
        input.folderId,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, folder.updatedAt),
      );
    });
  }

  public executeRestore(input: {
    folderId: EntityId;
    expectedRevision: number;
  }): Promise<Folder> {
    assertExpectedRevision(input.expectedRevision);
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const folder = await requireFolder(
        repositories,
        input.folderId,
        true,
      );
      return repositories.folders.restore(
        input.folderId,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, folder.updatedAt),
      );
    });
  }
}

export class TagApplicationService {
  public constructor(private readonly dependencies: CatalogDependencies) {}

  public executeCreate(input: {
    name: string;
    color?: string;
  }): Promise<Tag> {
    const tag = createTag({
      id: this.dependencies.ids.next(),
      name: input.name,
      createdAt: this.dependencies.clock.now(),
      ...(input.color === undefined ? {} : { color: input.color }),
    });
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      await repositories.tags.create(tag);
      return tag;
    });
  }

  public executeUpdate(input: {
    tagId: EntityId;
    expectedRevision: number;
    name?: string;
    color?: string | null;
  }): Promise<Tag> {
    assertExpectedRevision(input.expectedRevision);
    const patch: TagUpdatePatch = {
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(!("color" in input) ? {} : { color: input.color ?? null }),
    };
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const current = await repositories.tags.get(input.tagId);
      if (current === null) {
        throw new EntityNotFoundError("tag", input.tagId);
      }
      return repositories.tags.update(
        input.tagId,
        patch,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, current.updatedAt),
      );
    });
  }

  public executeTrash(input: {
    tagId: EntityId;
    expectedRevision: number;
  }): Promise<Tag> {
    assertExpectedRevision(input.expectedRevision);
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const tag = await requireTag(repositories, input.tagId, true);
      return repositories.tags.moveToTrash(
        input.tagId,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, tag.updatedAt),
      );
    });
  }

  public executeRestore(input: {
    tagId: EntityId;
    expectedRevision: number;
  }): Promise<Tag> {
    assertExpectedRevision(input.expectedRevision);
    return this.dependencies.unitOfWork.transaction(async (repositories) => {
      const tag = await requireTag(repositories, input.tagId, true);
      return repositories.tags.restore(
        input.tagId,
        input.expectedRevision,
        monotonicNow(this.dependencies.clock, tag.updatedAt),
      );
    });
  }
}

export interface ItemMetadataPatch {
  folderId?: EntityId | null;
  tagIds?: readonly EntityId[];
  favorite?: boolean;
  icon?: string | null;
  properties?: Readonly<Record<string, JsonValue>>;
}

export class ItemMetadataApplicationService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public execute(input: {
    itemId: EntityId;
    expectedRevision: number;
    patch: ItemMetadataPatch;
  }): Promise<KnowledgeItem> {
    assertExpectedRevision(input.expectedRevision);
    return this.unitOfWork.transaction(async (repositories) => {
      const item = await repositories.items.get(input.itemId);
      if (item === null) {
        throw new EntityNotFoundError("item", input.itemId);
      }
      await assertMetadataReferences(
        repositories,
        input.patch.folderId,
        input.patch.tagIds,
      );
      return repositories.items.update(
        input.itemId,
        toItemPatch(item, input.patch),
        input.expectedRevision,
        monotonicNow(this.clock, item.updatedAt),
      );
    });
  }
}

export interface BulkItemTarget {
  readonly itemId: EntityId;
  readonly expectedRevision: number;
}

export type BulkItemMutation =
  | { readonly kind: "move"; readonly folderId: EntityId | null }
  | {
      readonly kind: "tags";
      readonly tagIds: readonly EntityId[];
      readonly mode: "add" | "remove" | "replace";
    }
  | { readonly kind: "favorite"; readonly favorite: boolean }
  | { readonly kind: "trash" }
  | { readonly kind: "restore" };

export interface BulkMutationFailure {
  readonly itemId: EntityId;
  readonly message: string;
}

export interface BulkMutationResult {
  readonly policy: "atomic" | "best-effort";
  readonly succeeded: readonly KnowledgeItem[];
  readonly failures: readonly BulkMutationFailure[];
}

export class BulkItemApplicationService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public async execute(input: {
    targets: readonly BulkItemTarget[];
    mutation: BulkItemMutation;
    policy?: "atomic" | "best-effort";
  }): Promise<BulkMutationResult> {
    assertUniqueTargets(input.targets);
    const policy = input.policy ?? "atomic";
    if (policy === "atomic") {
      const succeeded = await this.unitOfWork.transaction((repositories) =>
        this.applyAll(repositories, input.targets, input.mutation)
      );
      return { policy, succeeded, failures: [] };
    }
    const succeeded: KnowledgeItem[] = [];
    const failures: BulkMutationFailure[] = [];
    for (const target of input.targets) {
      try {
        const result = await this.unitOfWork.transaction((repositories) =>
          this.applyOne(repositories, target, input.mutation)
        );
        succeeded.push(result);
      } catch (cause) {
        failures.push({
          itemId: target.itemId,
          message:
            cause instanceof Error ? cause.message : "The item update failed.",
        });
      }
    }
    return { policy, succeeded, failures };
  }

  private async applyAll(
    repositories: UnitOfWork["repositories"],
    targets: readonly BulkItemTarget[],
    mutation: BulkItemMutation,
  ): Promise<KnowledgeItem[]> {
    const results: KnowledgeItem[] = [];
    for (const target of targets) {
      results.push(await this.applyOne(repositories, target, mutation));
    }
    return results;
  }

  private async applyOne(
    repositories: UnitOfWork["repositories"],
    target: BulkItemTarget,
    mutation: BulkItemMutation,
  ): Promise<KnowledgeItem> {
    assertExpectedRevision(target.expectedRevision);
    const includeDeleted = mutation.kind === "restore";
    const item = await repositories.items.get(target.itemId, {
      includeDeleted,
    });
    if (item === null) {
      throw new EntityNotFoundError("item", target.itemId);
    }
    const now = monotonicNow(this.clock, item.updatedAt);
    if (mutation.kind === "trash") {
      return repositories.items.moveToTrash(
        item.id,
        target.expectedRevision,
        now,
      );
    }
    if (mutation.kind === "restore") {
      return repositories.items.restore(
        item.id,
        target.expectedRevision,
        now,
      );
    }
    if (mutation.kind === "move") {
      await assertMetadataReferences(
        repositories,
        mutation.folderId,
        undefined,
      );
      return repositories.items.update(
        item.id,
        { folderId: mutation.folderId },
        target.expectedRevision,
        now,
      );
    }
    if (mutation.kind === "favorite") {
      return repositories.items.update(
        item.id,
        {
          properties: {
            ...item.properties,
            [FAVORITE_PROPERTY]: mutation.favorite,
          },
        },
        target.expectedRevision,
        now,
      );
    }
    await assertMetadataReferences(
      repositories,
      undefined,
      mutation.tagIds,
    );
    const selected = new Set(mutation.tagIds);
    const tagIds =
      mutation.mode === "replace"
        ? [...selected]
        : mutation.mode === "add"
          ? [...new Set([...item.tags, ...selected])]
          : item.tags.filter((tagId) => !selected.has(tagId));
    return repositories.items.update(
      item.id,
      { tags: tagIds },
      target.expectedRevision,
      now,
    );
  }
}

function toItemPatch(
  item: KnowledgeItem,
  patch: ItemMetadataPatch,
): ItemUpdatePatch {
  return {
    ...(!("folderId" in patch) ? {} : { folderId: patch.folderId ?? null }),
    ...(patch.tagIds === undefined ? {} : { tags: [...new Set(patch.tagIds)] }),
    ...(!("icon" in patch) ? {} : { icon: patch.icon ?? null }),
    ...(patch.favorite === undefined && patch.properties === undefined
      ? {}
      : {
          properties: {
            ...item.properties,
            ...patch.properties,
            ...(patch.favorite === undefined
              ? {}
              : { [FAVORITE_PROPERTY]: patch.favorite }),
          } satisfies Readonly<Record<string, JsonValue>>,
        }),
  };
}

async function assertMetadataReferences(
  repositories: UnitOfWork["repositories"],
  folderId: EntityId | null | undefined,
  tagIds: readonly EntityId[] | undefined,
): Promise<void> {
  if (
    folderId !== undefined &&
    folderId !== null &&
    await repositories.folders.get(folderId) === null
  ) {
    throw new EntityNotFoundError("folder", folderId);
  }
  for (const tagId of new Set(tagIds ?? [])) {
    if (await repositories.tags.get(tagId) === null) {
      throw new EntityNotFoundError("tag", tagId);
    }
  }
}

async function requireFolder(
  repositories: UnitOfWork["repositories"],
  id: EntityId,
  includeDeleted: boolean,
): Promise<Folder> {
  const folder = await repositories.folders.get(id, includeDeleted);
  if (folder === null) throw new EntityNotFoundError("folder", id);
  return folder;
}

async function requireTag(
  repositories: UnitOfWork["repositories"],
  id: EntityId,
  includeDeleted: boolean,
): Promise<Tag> {
  const tag = await repositories.tags.get(id, includeDeleted);
  if (tag === null) throw new EntityNotFoundError("tag", id);
  return tag;
}

function assertUniqueTargets(targets: readonly BulkItemTarget[]): void {
  const seen = new Set<EntityId>();
  for (const target of targets) {
    if (seen.has(target.itemId)) {
      throw new ValidationError([
        { path: "targets", message: "item IDs must be unique" },
      ]);
    }
    seen.add(target.itemId);
  }
}

function assertExpectedRevision(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new ValidationError([
      {
        path: "expectedRevision",
        message: "must be a non-negative safe integer",
      },
    ]);
  }
}

function monotonicNow(clock: Clock, previous: number): number {
  return Math.max(clock.now(), previous);
}
